import { MaxWidthWrapper } from "@/components/maxWidthWrapper";
import { ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { Transition } from "framer-motion";
import { useWindowSize } from "../hooks";
import { useEffect, useState } from "react";
import { Logo } from "@/components/logo";

export const Hero = () => {
  return (
    <>
      <div className="absolute inset-0 -z-30 bg-radial-[at_90%_20%] from-secondary/50 to-transparent to-40%" />
      <div className="absolute inset-0 -z-30 bg-radial-[at_70%_80%] from-primary/50 to-transparent to-70%" />
      <div className="absolute top-2/3 right-0 left-0 -z-20 h-1/3 bg-gradient-to-b from-transparent to-background" />
      <Logo className="absolute -z-10 h-full p-24 text-primary/20 md:w-full dark:text-ring/20" />
      <MaxWidthWrapper className="mx-auto mt-16 h-[calc(100dvh-44px)] max-w-7xl px-6 pt-24 pb-24 sm:pb-32 lg:flex lg:px-8 lg:pt-40">
        <div className="mx-auto max-w-4xl pt-24 md:mx-0 md:shrink-0">
          <div className="relative md:translate-y-1/2 lg:translate-y-1/2 xl:translate-y-1/2">
            <motion.div
              initial={{ y: 25, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.25, ease: "easeInOut" }}
              className="absolute top-12 -z-10 h-3/4 w-3/4 bg-background blur-3xl"
            />
            <motion.h1
              initial={{ y: 25, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.25, delay: 0.25, ease: "easeInOut" }}
              className="mt-10 text-5xl font-bold tracking-tight text-foreground sm:text-7xl"
            >
              <motion.div
                initial={{ x: 2000 }}
                animate={{ x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                Lightning Fast,
              </motion.div>
              <motion.div
                initial={{ x: -2000 }}
                animate={{ x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <span className="text-primary italic">Privacy-First</span> Technology
              </motion.div>
            </motion.h1>
            <motion.p
              initial={{ y: 25, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.25, delay: 0.5, ease: "easeInOut" }}
              className="my-8 text-lg font-medium text-pretty text-foreground sm:text-xl/8"
            >
              Streamline your verification process. Eliminate the lines and save time.
            </motion.p>
            <motion.a
              initial={{ y: 25, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.25, delay: 0.75, ease: "easeInOut" }}
              href="/demo-request"
              className="disabled:opacity-50&_svg]:pointer-events-none inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-full bg-primary px-8 text-base font-medium whitespace-nowrap text-primary-foreground shadow-md transition-transform duration-300 hover:translate-y-[-2px] hover:bg-primary/90 hover:shadow-lg focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            >
              Request a Demo
              <ArrowRight className="ml-2 size-4" />
            </motion.a>
          </div>
        </div>
        <SpinningFaces />
      </MaxWidthWrapper>
    </>
  );
};

const transitionSettings: Transition = {
  repeat: Infinity,
  repeatType: "loop",
  duration: 50,
  ease: "linear",
} as const;

const SpinningFaces = () => {
  const { width } = useWindowSize();
  const [dimensions, setDimensions] = useState<DimensionConstants>(large);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    if (!width) return;

    if (width < breakPoints.sm) {
      setDimensions(small);
    } else if (width < breakPoints.md) {
      setDimensions(medium);
    } else if (width < breakPoints.lg) {
      setDimensions(large);
    } else {
      setDimensions(xLarge);
    }
  }, [width]);

  if (!isMounted) {
    return <div className="absolute top-0 right-0 hidden h-1/2 w-1/2 place-content-center" />;
  }

  const containerWidth =
    dimensions.radiusToCenterOfIcons + dimensions.iconWrapperWidth + dimensions.ringPadding;
  const containerHeight =
    dimensions.radiusToCenterOfIcons + dimensions.iconWrapperWidth + dimensions.ringPadding;
  const innerWidth =
    dimensions.radiusToCenterOfIcons - dimensions.iconWrapperWidth - dimensions.ringPadding;
  const innerHeight =
    dimensions.radiusToCenterOfIcons - dimensions.iconWrapperWidth - dimensions.ringPadding;

  return (
    <div
      style={{
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
      }}
      className="absolute top-0 right-0 hidden place-content-center md:grid md:translate-x-1/3 md:-translate-y-1/3"
    >
      <motion.div
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={transitionSettings}
        onUpdate={(latest) => {
          if (typeof latest.rotate === "number") {
            setRotationAngle(latest.rotate);
          }
        }}
        style={{
          width: `${innerWidth}px`,
          height: `${innerHeight}px`,
        }}
        className="relative grid place-items-center rounded-full shadow-sm"
      >
        {galleryData.map((icon, idx) => {
          const degrees = (360 / galleryData.length) * idx;
          const marginTop = dimensions.radiusToCenterOfIcons * Math.sin(degreesToRadians(degrees));
          const marginLeft = dimensions.radiusToCenterOfIcons * Math.cos(degreesToRadians(degrees));
          const actualDegrees = (degrees + rotationAngle) % 360;
          const lowerBound = dimensions === small ? 100 : 115;
          const isInTargetQuadrant = actualDegrees > lowerBound && actualDegrees < 220;

          return (
            <motion.div
              key={idx}
              style={{
                marginTop: `${marginTop}px`,
                marginLeft: `${marginLeft}px`,
                width: `${dimensions.iconWrapperWidth}px`,
                height: `${dimensions.iconWrapperWidth}px`,
                transform: "none",
              }}
              initial={{ rotate: 0 }}
              animate={{ rotate: -360 }}
              transition={transitionSettings}
              className="absolute grid place-items-center rounded-full shadow-lg"
            >
              <motion.img src={icon.src} className="h-full w-full rounded-full" />
              <AnimatePresence>
                {isInTargetQuadrant ? (
                  <motion.span
                    className="absolute top-0 left-0 flex h-full w-full items-end justify-center rounded-full bg-background/40"
                    key={`icon-check-${idx}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.125 }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="-mb-1 size-5 text-green-500 md:-mb-5 md:size-16"
                    >
                      {/* draw check icon */}
                      <motion.path
                        d="M5 13l4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        exit={{ pathLength: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      />
                    </svg>
                  </motion.span>
                ) : null}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

const degreesToRadians = (degrees: number) => {
  return degrees * (Math.PI / 180);
};

type DimensionConstants = {
  // Distance from the center of circle to center of icons
  radiusToCenterOfIcons: number;
  // Width of the icon circles
  iconWrapperWidth: number;
  // Padding between the icon circles and the inner and outer rings
  ringPadding: number;
};

const small: DimensionConstants = {
  radiusToCenterOfIcons: 250,
  iconWrapperWidth: 45,
  ringPadding: 10,
} as const;

const medium: DimensionConstants = {
  radiusToCenterOfIcons: 325,
  iconWrapperWidth: 70,
  ringPadding: 30,
} as const;

const large: DimensionConstants = {
  radiusToCenterOfIcons: 475,
  iconWrapperWidth: 90,
  ringPadding: 40,
} as const;

const xLarge: DimensionConstants = {
  radiusToCenterOfIcons: 750,
  iconWrapperWidth: 110,
  ringPadding: 50,
} as const;

const breakPoints = {
  sm: 480,
  md: 768,
  lg: 1024,
} as const;

const galleryData = [
  { id: 1, src: "/static/1.png" },
  { id: 2, src: "/static/2.png" },
  { id: 3, src: "/static/3.png" },
  { id: 4, src: "/static/4.png" },
  { id: 5, src: "/static/5.png" },
  { id: 6, src: "/static/6.png" },
  { id: 7, src: "https://ca.slack-edge.com/T055RTHK5SP-U05M1B7EBCZ-ca3fcf123a08-512" },
  { id: 8, src: "/static/7.png" },
  { id: 9, src: "/static/8.png" },
  { id: 10, src: "/static/9.png" },
  { id: 11, src: "/static/10.png" },
  { id: 12, src: "/static/11.png" },
  { id: 13, src: "/static/13.png" },
  { id: 14, src: "https://randomuser.me/api/portraits/men/83.jpg" },
  { id: 15, src: "https://randomuser.me/api/portraits/women/10.jpg" },
] as const;
